/**
 * Shared transient-failure classification + retry for calls to Cloud Functions —
 * both `httpsCallable` (Firebase callable codes) and raw `fetch`/`fetchWithTimeout`
 * calls to `onRequest` endpoints.
 *
 * CORRECTED 2026-09-28: a client-side timeout does NOT mean the server did nothing.
 * The request may still complete after the client gave up (measured: one CRM send
 * click became three customer emails that way). Re-invoking is safe ONLY for a READ,
 * or for a write that carries an idempotency key the server enforces.
 * `withTransientRetry` may therefore wrap only a read or a keyed write; never a bare
 * send, create or charge. Permanent errors (permission-denied,
 * invalid-argument, unauthenticated, failed-precondition, 4xx other than 429) are
 * NOT retried — they fail fast. Without this, a cold start (see #2360, ~9.7s with
 * no `minInstances` on most callables) surfaces to the user as a generic "Network
 * error — check your connection", which is both wrong (their connection is fine)
 * and avoidable (the first retry almost always lands on a now-warm instance).
 */

import { openRetryWindow, noteRecovered } from '../datahub/errorLogging/retryWindow';
import { fetchWithTimeout, TimeoutError, type FetchWithTimeoutOptions } from './fetchWithTimeout';

/** Firebase callable (`httpsCallable`) error codes worth retrying. */
const TRANSIENT_CALLABLE_CODES = [
  'internal',
  'unavailable',
  'deadline-exceeded',
  'aborted',
  'resource-exhausted',
] as const;

/** HTTP status codes worth retrying — rate-limited or upstream/service unavailable, all cold-start-adjacent. */
const TRANSIENT_HTTP_STATUSES = new Set([429, 502, 503, 504]);

/** Error thrown by {@link fetchJsonWithRetry} for a non-OK HTTP response; carries the status for classification. */
export class HttpError extends Error {
  readonly status: number;
  /** True when `message` came from the response body's `error` field, false when it's the generic `HTTP <status>` fallback. */
  readonly hasServerMessage: boolean;
  constructor(status: number, message: string, hasServerMessage: boolean = true) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
    this.hasServerMessage = hasServerMessage;
  }
}

/** True when a rejected `httpsCallable` error is a transient (retryable) 5xx-class failure. */
export function isTransientCallableError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const code = (error as { code?: unknown }).code;
  if (typeof code !== 'string') return false;
  // Firebase surfaces callable codes as `functions/<code>`; match the suffix too.
  return TRANSIENT_CALLABLE_CODES.some((c) => code === c || code.endsWith(`/${c}`));
}

/** True when an HTTP status code (from a raw fetch to an `onRequest` Cloud Function) is transient. */
export function isTransientHttpStatus(status: number): boolean {
  return TRANSIENT_HTTP_STATUSES.has(status);
}

/**
 * True when a caught error from a `fetch`/`fetchWithTimeout` call to a Cloud
 * Function is transient and safe to automatically retry: an {@link HttpError}
 * carrying a transient status (429/502/503/504 — the request was rejected
 * before the handler ran, so nothing executed server-side), or a network-level
 * failure (the request never reached the server at all).
 *
 * Deliberately EXCLUDES client-side timeout ({@link TimeoutError}): aborting the
 * `fetch` client-side does not stop the Cloud Function's execution server-side,
 * so a timeout does not mean the request failed — the handler may still be
 * running (or may have already completed) when the client gives up. Auto-retrying
 * a timeout on a paid/non-idempotent call (e.g. a Gemini generation call) risks
 * silently doubling cost or side effects. Callers should surface a distinct
 * "taking longer than usual" message for `TimeoutError` (see
 * {@link describeFetchFailure}) without retrying.
 */
export function isTransientFetchError(error: unknown): boolean {
  if (error instanceof HttpError) return isTransientHttpStatus(error.status);
  if (error instanceof TypeError) return true; // fetch network failure (e.g. "Failed to fetch")
  return false;
}

/**
 * Invoke an async operation with exponential-backoff retries on transient
 * failures. Works for both `httpsCallable` errors (pass `isTransientCallableError`,
 * the default) and raw `fetch` failures (pass `isTransientFetchError`).
 */
export async function withTransientRetry<T>(
  operation: () => Promise<T>,
  {
    retries = 2,
    baseDelayMs = 600,
    isTransient = isTransientCallableError,
    endpoint,
  }: {
    retries?: number;
    baseDelayMs?: number;
    isTransient?: (error: unknown) => boolean;
    /**
     * The URL or callable name this operation calls. When given, a transient failure of EARLIER attempts is recorded as a
     * breadcrumb instead of a Sentry error (#4630); the final attempt still reports, so an exhausted budget is one error.
     */
    endpoint?: string;
  } = {},
): Promise<T> {
  const retryWin = endpoint ? openRetryWindow(endpoint, retries + 1) : null;
  let lastError: unknown;
  try {
    for (let attempt = 0; attempt <= retries; attempt++) {
      retryWin?.beginAttempt(attempt);
      try {
        const result = await operation();
        retryWin?.recovered();
        if (endpoint && attempt > 0) noteRecovered(endpoint, attempt + 1);
        return result;
      } catch (error) {
        lastError = error;
        if (attempt === retries || !isTransient(error)) {
          // No later attempt: a failure the network hook held back for this window is the person's failure after all.
          retryWin?.abandon();
          throw error;
        }
        retryWin?.endAttempt();
        await new Promise((resolve) => setTimeout(resolve, baseDelayMs * 2 ** attempt));
      }
    }
    throw lastError;
  } finally {
    retryWin?.close();
  }
}

/**
 * `fetch` + parse JSON from an `onRequest` Cloud Function, with automatic retry
 * on transient failures (network error, 429/502/503/504) via
 * {@link withTransientRetry}. A client-side timeout is NOT auto-retried — see
 * {@link isTransientFetchError}. Throws {@link HttpError} on a non-OK response
 * (after retries are exhausted) so callers can classify/report the failure
 * accurately instead of a blanket "Network error".
 */
export async function fetchJsonWithRetry<T = unknown>(
  url: string,
  options: FetchWithTimeoutOptions & {
    retries?: number;
    retryBaseDelayMs?: number;
    /**
     * Whether a 429 should be retried. Default true (a 429 from a cold or
     * bursty backend usually clears).
     *
     * Pass `false` for an endpoint whose 429 means "you have spent a quota
     * window": retrying there consumes the same quota again, so three attempts
     * cost triple and the victims themselves keep the window pinned. That is
     * a non-idempotent side effect inside a retry loop — see the forms-AI
     * credential gate, whose quota increments on rejected attempts too.
     */
    retryOn429?: boolean;
  } = {},
): Promise<T> {
  const { retries, retryBaseDelayMs, retryOn429 = true, ...fetchOptions } = options;
  const isTransient = retryOn429
    ? isTransientFetchError
    : (error: unknown) =>
      !(error instanceof HttpError && error.status === 429) && isTransientFetchError(error);
  return withTransientRetry(
    async () => {
      const response = await fetchWithTimeout(url, fetchOptions);
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        const hasServerMessage = typeof body.error === 'string' && body.error.length > 0;
        throw new HttpError(response.status, hasServerMessage ? body.error : `HTTP ${response.status}`, hasServerMessage);
      }
      try {
        return await response.json() as T;
      } catch {
        throw new HttpError(response.status, 'Response was not valid JSON (empty or malformed body)', false);
      }
    },
    { retries, baseDelayMs: retryBaseDelayMs, isTransient, endpoint: url },
  );
}

/**
 * User-facing message for a failure from {@link fetchJsonWithRetry} /
 * {@link withTransientRetry}, once retries are exhausted. Distinguishes a
 * still-cold-after-retries backend (rare — most cold starts clear on the first
 * retry) from an actual connectivity problem, instead of always blaming
 * "your connection".
 */
export function describeFetchFailure(error: unknown): string {
  if (error instanceof TimeoutError) {
    return 'The service is taking longer than usual to respond. Please try again.';
  }
  // A server-authored message on a rate-limit response is accurate and
  // actionable; the generic "starting up" blurb below is neither. Checking
  // transience first would discard it — 429 is in the transient set.
  if (error instanceof HttpError && error.status === 429 && error.hasServerMessage) {
    return error.message;
  }
  if (isTransientFetchError(error)) {
    return 'The service is starting up. Please try again in a moment.';
  }
  if (error instanceof Error && error.message) return error.message;
  return 'Network error — check your connection';
}
